/* eslint-disable max-lines */
/* eslint-disable camelcase */
/* eslint-disable react/require-default-props */
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import { fetchAllCampaignRuleOutcomes, fetchFiltersCounts } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { EXTERNAL_LINK_WHITE_ICON } from 'constants/commonIcons';
import { SEARCH_ICON } from 'constants/filterIcons';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useDispatch, useSelector } from 'react-redux';
import { getCampaignRuleOutcomesList } from 'selectors/campaignRuleOutcomes';
import { Selector } from 'types';
import logEvent from 'utils/analytics';
import { triggerURIDownload } from 'utils/triggerDownload';
import ShowLabels from './ShowLabels';
import ShowLabelsDashboard from './ShowLabelsDashboard';

type MinimumResource = {
  uuid: string;
  name?: string | null;
  email?: string | null;
  color?: string | null;
  envelopes_count?: number;
  count?: number;
};

type ResourceFilterProps<T extends MinimumResource, A> = {
  resource: string;
  className?: string;
  filterKey: string;
  fetchAction?: A;
  getResourceList: Selector<T[]>;
  getResourceCount: Selector<number>;
  getLoading: Selector<boolean>;
  fetchLabelsResourceList?: A;
  title?: string;
  nameKey: keyof T;
  secondNameKey?: keyof T;
  itemField?: keyof T;
  allowExclude?: boolean;
  dataTestid?: string;
  isRule?: boolean;
  colorChip?: boolean;
  isOpening?: boolean;
  notTreeLabel?: boolean;
  filterCountsField?: string;

  treeContext: React.Context<FiltersTreeContext>;
};

const BackendResourceFilter = <
  T extends MinimumResource,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  A extends ActionCreatorWithOptionalPayload<any, string>
>(
  props: ResourceFilterProps<T, A>
): ReturnType<React.FC> => {
  const {
    className,
    resource,
    fetchAction,
    filterKey,
    itemField = 'uuid',
    getResourceList,
    getLoading,
    fetchLabelsResourceList,
    title = '',
    nameKey,
    secondNameKey,
    allowExclude = true,
    dataTestid,
    isRule = false,
    colorChip = false,
    isOpening,
    getResourceCount,
    notTreeLabel = false,
    filterCountsField,

    treeContext,
  } = props;

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [offset, setOffset] = useState(0);
  const [checkedStatus, setCheckedStatus] = useState(false);
  const [isFirsLoad, setIsFirstLoad] = useState(true);

  const inputRef = useRef<HTMLInputElement>(null);

  const allData = useSelector(getResourceList);
  const outcomes = useSelector(getCampaignRuleOutcomesList);
  const totalCount = useSelector(getResourceCount);

  const loading = useSelector(getLoading);

  const {
    tree,
    treeParams,
    filtersSearchString,
    handleAddValueToTree,
    handleRemoveValueFromTree,
    handleRemoveFieldFromTree,
    handleAddAllValuesToField,
  } = useContext(treeContext);

  const notFilterData = treeParams[`not_${filterKey}`] ?? [];
  const filterData = treeParams[filterKey] ?? [];

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  useEffect(() => {
    if (fetchLabelsResourceList && (filterData.length > 0 || notFilterData.length > 0)) {
      dispatch(fetchLabelsResourceList({ uuids: [...filterData, ...notFilterData] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterData.length, notFilterData.length]);

  useEffect(() => {
    if (filterCountsField && isOpen) {
      dispatch(
        fetchFiltersCounts({ fields: [filterCountsField], filterSearchString: filtersSearchString })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filtersSearchString, isOpen]);

  // first time opening
  useEffect(() => {
    if (isOpen && fetchAction != null && isFirsLoad) {
      setIsFirstLoad(false);
      dispatch(
        fetchAction({
          limit: 25,
          include_envelopes_count: false,
          searchValue: debouncedSearchValue,
          offset: 0,
          filtersSearchString,
        })
      );
      setOffset(0);
      if (isRule) {
        dispatch(fetchAllCampaignRuleOutcomes({ limit: -1 }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen]);

  // general changes
  useEffect(() => {
    if (isOpen && fetchAction != null && !filterCountsField) {
      setIsFirstLoad(false);
      dispatch(
        fetchAction({
          limit: 25,
          include_envelopes_count: false,
          searchValue: debouncedSearchValue,
          offset: 0,
          filtersSearchString,
        })
      );
      setOffset(0);
      if (isRule) {
        dispatch(fetchAllCampaignRuleOutcomes({ limit: -1 }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchAction, isRule, filtersSearchString]);

  // for search changes
  useEffect(() => {
    if (isOpen && fetchAction && !isFirsLoad) {
      dispatch(
        fetchAction({
          limit: 25,
          include_envelopes_count: false,
          searchValue: debouncedSearchValue,
          offset: 0,
          filtersSearchString,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  // general opening
  useEffect(() => {
    if (isOpen && fetchAction && !isFirsLoad) {
      dispatch(
        fetchAction({
          limit: 25,
          include_envelopes_count: false,
          searchValue: debouncedSearchValue,
          offset: 0,
          filtersSearchString,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchValue(searchValue), 300);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    if (isOpening) {
      inputRef.current?.focus();
    }
  }, [inputRef, isOpening]);

  useEffect(() => {
    if (notFilterData.length && filterData.length) {
      setCheckedStatus(true);
    }
  }, [filterData.length, notFilterData.length]);

  const handleButtonClick = (): void => {
    setIsOpen(true);
  };

  const handleDelete = (labelId: string, exclude: boolean): void => {
    if (exclude) {
      handleRemoveValueFromTree(labelId, `not_${filterKey}`);
    } else {
      handleRemoveValueFromTree(labelId, filterKey);
    }
  };

  const handleInputChange = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: T
  ): void => {
    logEvent(`envelopes-list-filter-${title.toLowerCase()}`);

    const id = item.uuid;
    const value = item[itemField];
    const tagName = typeof value === 'string' ? value : id;

    const { shiftKey } = event.nativeEvent;

    let exclude = shiftKey;
    if (allowExclude === false) exclude = false;

    if (exclude && filterData.includes(tagName)) return;

    let tagsNames = !exclude ? [...filterData] : [...notFilterData];

    event.currentTarget.dataset.checked =
      event.currentTarget.dataset.checked === 'true' ? 'false' : 'true';

    if (event.currentTarget.dataset.checked === 'true') {
      tagsNames.push(tagName);
      const label = item[nameKey] as unknown as string;
      handleAddValueToTree(
        tagName,
        !exclude ? filterKey.replace('_and', '') : `not_${filterKey}`.replace('_and', ''),
        label
      );
    } else {
      tagsNames = tagsNames.filter((s) => s !== tagName);
      handleDelete(tagName, notFilterData.includes(tagName));
    }

    event.stopPropagation();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e?.target.value.toLowerCase();
    setSearchValue(value);
  };

  const handleLoadMore = (): void => {
    if (isOpen && fetchAction != null) {
      dispatch(
        fetchAction({
          limit: 25,
          include_envelopes_count: false,
          searchValue: debouncedSearchValue,
          offset: offset + 25,
          filtersSearchString,
        })
      );
      setOffset((o) => o + 25);
    }
  };

  const handleInputChangeAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    logEvent(`envelopes-filter-all-${resource}`);

    const data = [...allData];
    const queryIds = data.reduce(
      (acc, item) => [...acc, { value: item.uuid, label: item[nameKey] }] as never,
      []
    );

    if (event.target.checked) {
      if (filterData.length && !notFilterData.length) {
        handleAddAllValuesToField(filterKey.replace('_and', ''), queryIds);
      } else if (notFilterData.length && !filterData.length) {
        handleAddAllValuesToField(`not_${filterKey.replace('_and', '')}`, queryIds);
      }

      setCheckedStatus(true);
    } else {
      if (!filterData.length && !notFilterData.length) {
        dispatch(handleRemoveFieldFromTree(filterKey.replace('_and', '')));
      } else if (notFilterData.length && !filterData.length) {
        dispatch(handleRemoveFieldFromTree(`not_${filterKey.replace('_and', '')}`));
      } else {
        dispatch(handleRemoveFieldFromTree(filterKey.replace('_and', '')));
        dispatch(handleRemoveFieldFromTree(`not_${filterKey.replace('_and', '')}`));
      }
      setCheckedStatus(false);
    }
  };

  const handleExportCounts = (): void => {
    const shapedData = allData.map((d) => ({
      name: d[nameKey],
      count: !d.count ? 0 : d.count,
    }));

    const stringData = shapedData.reduce((acc, val) => `${acc}${val.name},${val.count}\n`, '');
    const formattedData = `data:text/csv;charset=utf-8,Name,Count\n${stringData}`;

    triggerURIDownload(formattedData, title);
  };

  const getSelectAllStyle = (): string => {
    if (notFilterData.length && filterData.length) {
      return 'checkbox-select-all-filter';
    }
    if (notFilterData.length && !filterData.length) {
      return 'litlingo-checkbox-negated';
    }
    return '';
  };

  return (
    <>
      <div ref={wrapperRef} className={`flex flex-col ${className || ''}"`}>
        <div className="relative flex flex-row justify-start items-center w-full min-h-8 rounded p-1.5 bg-litlingo-success-light bg-opacity-10">
          {filterData.length || notFilterData.length ? (
            <div className="flex items-center h-5 ml-0.5">
              <input
                data-testid="select-all-checkbox"
                type="checkbox"
                className={`form-checkbox litlingo-checkbox  h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${getSelectAllStyle()}`}
                onChange={handleInputChangeAll}
                checked={checkedStatus}
              />
            </div>
          ) : (
            <div className="w-5">{SEARCH_ICON('white')}</div>
          )}
          <input
            id="search"
            name="search"
            ref={inputRef}
            className="max-h-5 w-full text-white ml-1 font-normal text-base bg-transparent placeholder-white placeholder-italic"
            placeholder={`Search ${title}`}
            data-testid={dataTestid}
            type="text"
            onFocus={handleButtonClick}
            onChange={handleSearchChange}
            autoComplete="off"
          />
        </div>
        {isOpen && (
          <>
            <LoadingOverlayWrapper active={loading} spinner={<LoadingIndicator />} fadeSpeed={0}>
              <div className="w-full max-h-filter z-10 overflow-auto bg-litlingo-gray-1 custom-scrollbar">
                <fieldset className="m-2 min-h-5">
                  {allData.map((item, idx) => {
                    const ruleOutcome = outcomes.find((outcome) => outcome.rule_uuid === item.uuid);

                    if (
                      isRule &&
                      (!ruleOutcome || (ruleOutcome && ruleOutcome.campaign?.deleted_at))
                    )
                      return null;
                    const value = item[itemField];
                    const tagName = typeof value === 'string' ? value : item.uuid;

                    return (
                      <div key={item.uuid} className={idx !== 0 ? 'mt-2' : ''}>
                        <div
                          id={`${filterKey}-${item.uuid}`}
                          role="button"
                          aria-hidden
                          className="relative flex items-center cursor-pointer"
                          data-checked={[...filterData, ...notFilterData].includes(tagName)}
                          onClick={(event): void => handleInputChange(event, item)}
                        >
                          <div className="w-full relative flex flex-col items-start justify-center text-small cursor-pointer">
                            <div
                              className={`w-full relative flex flex-row gap-1 justify-start ${
                                secondNameKey ? 'items-start' : 'items-start'
                              }`}
                            >
                              <input
                                id={`${filterKey}-${item.uuid}`}
                                data-testid={`${item.uuid}-checkbox`}
                                type="checkbox"
                                className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${
                                  notFilterData.includes(tagName) ? 'litlingo-checkbox-negated' : ''
                                }`}
                                checked={
                                  filterData.includes(tagName) || notFilterData.includes(tagName)
                                }
                              />
                              {colorChip && (
                                <div
                                  className="rounded-full min-w-3 min-h-3 mr-2"
                                  style={{ backgroundColor: item.color || '' }}
                                />
                              )}
                              <div className="w-full flex flex-col">
                                <label
                                  htmlFor={`${filterKey}-${item.uuid}`}
                                  className="pt-0.5 w-full flex flex-row justify-between gap-1 text-small cursor-pointer"
                                >
                                  <div className="flex flex-col">
                                    <span
                                      className="w-full select-none"
                                      style={{ wordBreak: 'break-word' }}
                                    >
                                      {item[nameKey]}
                                    </span>
                                    {secondNameKey && (
                                      <span className="text-litlingo-gray-5 text-xxs font-normal select-none break-all">
                                        {item[secondNameKey]}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-litlingo-primary">
                                    {!item.count && item.count !== 0 ? '-' : item.count}
                                  </span>
                                </label>
                              </div>
                            </div>
                            {ruleOutcome && (
                              <span className="text-xs text-gray-500 select-none break-all">
                                {ruleOutcome.campaign?.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </fieldset>
                {offset + 25 < totalCount - 1 && (
                  <button
                    type="button"
                    className="w-full py-1 text-small border-t border-litlingo-gray-2 focus:outline-none"
                    onClick={handleLoadMore}
                  >
                    <span className="text-litlingo-primary">Load More</span>
                  </button>
                )}
              </div>
            </LoadingOverlayWrapper>

            {allowExclude && isOpen && (
              <div className="mt-2 -mb-2 flex flex-row justify-between items-center">
                <button type="button" className="focus:outline-none" onClick={handleExportCounts}>
                  {EXTERNAL_LINK_WHITE_ICON}
                </button>
                <span className="text-white font-normal text-xss">Shift + click to exclude</span>
              </div>
            )}
          </>
        )}
        {!isOpen && !notTreeLabel && (
          <ShowLabels
            handleDelete={handleDelete}
            getResourceKey={filterKey}
            filterData={filterData as string[]}
            notFilterData={notFilterData as string[]}
            tree={tree}
          />
        )}
        {!isOpen && notTreeLabel && (
          <ShowLabelsDashboard
            handleDelete={handleDelete}
            getResourceList={getResourceList}
            filterData={filterData as string[]}
            notFilterData={notFilterData as string[]}
            nameKey={nameKey}
            itemField={itemField !== 'uuid' ? itemField : undefined}
          />
        )}
      </div>
    </>
  );
};

export default BackendResourceFilter;
